import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Hero from '@/components/Hero';
import HeroSocialIcons from '@/components/HeroSocialIcons';
import ZacksManifestoButton from '@/components/ZacksManifestoButton';
import HowItWorks from '@/components/HowItWorks';
import Pricing from '@/components/Pricing';
import TravelPacks from '@/components/TravelPacks';
import PrivacyPacks from '@/components/PrivacyPacks';
import UnlimitedPacks from '@/components/UnlimitedPacks';
import VirtualNumbers from '@/components/VirtualNumbers';
import WhyChoosePP from '@/components/WhyChoosePP';
import WhyChoose from '@/components/WhyChoose';
import PrivacySecurity from '@/components/PrivacySecurity';
import FAQ from '@/components/FAQ';
import ResponsibleUse from '@/components/ResponsibleUse';
import HomepageTrustStrip from '@/components/HomepageTrustStrip';
import TravelEsimDestinations from '@/components/TravelEsimDestinations';
import MetaPixel from '@/components/MetaPixel';
import GoogleAnalytics from '@/components/GoogleAnalytics';
import TikTokPixel from '@/components/TikTokPixel';
import CookieBanner from '@/components/CookieBanner';
import FirstPartyFunnelTelemetry from '@/components/FirstPartyFunnelTelemetry';

import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsConditions from '@/pages/TermsConditions';
import AcceptableUsePolicy from '@/pages/AcceptableUsePolicy';
import CustomerCareComplaints from '@/pages/CustomerCareComplaints';
import RefundPolicy from '@/pages/RefundPolicy';
import VulnerablePersonsPolicy from '@/pages/VulnerablePersonsPolicy';
import AICatalogue from '@/pages/AICatalogue';
import ADRPage from '@/pages/ADRPage';
import CookiePolicyPage from '@/pages/CookiePolicyPage';
import ExternalPortalRedirect from '@/pages/ExternalPortalRedirect';
import SuccessPage from '@/pages/SuccessPage';
import ZacksManifesto from '@/pages/ZacksManifesto';
import ZeroLogManifesto from '@/pages/ZeroLogManifesto';
import HowItWorksPage from '@/pages/HowItWorksPage';
import AboutUs from '@/pages/AboutUs';
import NotFound from '@/pages/NotFound';
import ServicePage from '@/pages/ServicePage';
import IntentLandingPage from '@/pages/IntentLandingPage';
import PrivacyArchitecture from '@/pages/PrivacyArchitecture';
import FAQPage from '@/pages/FAQPage';
import TravelCampaignPage from '@/pages/TravelCampaignPage';

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  const previousPathname = useRef(pathname);

  useEffect(() => {
    const previousSetting = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = previousSetting; };
  }, []);

  useEffect(() => {
    const returningToHomepage = pathname === '/' && previousPathname.current !== '/';
    previousPathname.current = pathname;

    if (hash) {
      let attempts = 0;
      const scrollToTarget = () => {
        const target = document.getElementById(hash.slice(1));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        attempts += 1;
        if (attempts < 30) window.setTimeout(scrollToTarget, 50);
      };
      window.requestAnimationFrame(scrollToTarget);
    } else if (returningToHomepage) {
      window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

const LegacyTermsRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/terms${location.search}${location.hash}`} replace />;
};

const homepageTitle = 'Pay As You Go VPN, eSIM Data & Virtual Numbers | PhantomPathVPN';
const homepageDescription = 'No subscriptions. No mandatory accounts. Pay as you go VPN passes, global travel eSIM data and UK & US virtual numbers. No retained VPN traffic logs.';
const homepageUrl = 'https://phantompathvpn.com/';
const homepageWebsiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${homepageUrl}#website`,
  name: 'PhantomPathVPN',
  alternateName: 'PhantomPath VPN',
  url: homepageUrl,
  description: homepageDescription,
  publisher: { '@id': `${homepageUrl}#organization` },
};

const LandingPage = ({ initialEsimCatalogue }) => (
  <main className="landing-page-grid">
    <Helmet>
      <title>{homepageTitle}</title>
      <meta name="description" content={homepageDescription} />
      <link rel="canonical" href={homepageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={homepageUrl} />
      <meta property="og:title" content={homepageTitle} />
      <meta property="og:description" content={homepageDescription} />
      <meta name="twitter:title" content={homepageTitle} />
      <meta name="twitter:description" content={homepageDescription} />
      <script type="application/ld+json">{JSON.stringify(homepageWebsiteSchema)}</script>
    </Helmet>
    <Hero />
    <HomepageTrustStrip />
    <ZacksManifestoButton />
    <ResponsibleUse />
    <WhyChoosePP />
    <HowItWorks />
    <Pricing />
    <TravelPacks />
    <TravelEsimDestinations catalogue={initialEsimCatalogue} />
    <PrivacyPacks />
    <UnlimitedPacks />
    <VirtualNumbers />
    <WhyChoose />
    <PrivacySecurity />
    <FAQ />
    <HeroSocialIcons />
  </main>
);

const App = ({ initialEsimCatalogue = null }) => {
  const { pathname } = useLocation();
  const publicLightPage = !['/portal', '/success'].includes(pathname);
  const useContinuousBlackBackground = !publicLightPage;
  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://phantompathvpn.com/#organization',
    name: 'PhantomPathVPN',
    legalName: 'PHANTOMPATHVPN LTD',
    alternateName: 'PhantomPathVPN Ltd',
    url: 'https://phantompathvpn.com/',
    logo: 'https://phantompathvpn.com/phantompath-logo-transparent.png',
    description: homepageDescription,
    foundingDate: '2026-01-12',
    identifier: {
      '@type': 'PropertyValue',
      propertyID: 'UK Companies House company number',
      value: '16958507',
      url: 'https://find-and-update.company-information.service.gov.uk/company/16958507',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '6 Roger Street',
      addressLocality: 'Treboeth',
      addressRegion: 'Swansea',
      postalCode: 'SA5 9AS',
      addressCountry: 'GB',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      telephone: '+44-333-313-0127',
      email: 'support@phantompathvpn.com',
      availableLanguage: 'English',
    },
    sameAs: [
      'https://find-and-update.company-information.service.gov.uk/company/16958507',
      'https://www.instagram.com/phantompathvpn',
      'https://www.tiktok.com/@phantompathvpn.com',
    ],
  };
  return (
    <>
      <MetaPixel />
      <GoogleAnalytics />
      <TikTokPixel />
      <CookieBanner />
      <FirstPartyFunnelTelemetry />
      <ScrollManager />
      <Helmet>
        <html lang="en" />
        {!['/portal', '/success', '/ai-catalogue'].includes(pathname) && (
          <link rel="canonical" href={`https://phantompathvpn.com${pathname === '/' ? '/' : pathname}`} />
        )}
        <meta name="keywords" content="pay as you go VPN, on demand VPN, travel eSIM, virtual number, privacy pack, secure connection" />
        <meta property="og:site_name" content="PhantomPathVPN" />
        <meta property="og:image" content="https://phantompathvpn.com/og.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://phantompathvpn.com/og.png" />
        <script type="application/ld+json">{JSON.stringify(organization)}</script>
      </Helmet>
      <div className={`flex min-h-screen flex-col bg-[#050b14] font-sans text-white selection:bg-[#2dd4bf] selection:text-[#020617] ${pathname === '/' ? 'rebrand-shell' : ''} ${publicLightPage ? 'public-light-shell' : ''} ${pathname === '/portal' ? 'portal-light-shell' : ''}`}>
        <Header />
        <div className="flex-grow">
          <Routes>
            <Route path="/" element={<LandingPage initialEsimCatalogue={initialEsimCatalogue} />} />
            <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
            <Route path="/portal" element={<ExternalPortalRedirect />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/zacks-manifesto" element={<ZacksManifesto />} />
            <Route path="/learn/zacks-manifesto" element={<Navigate to="/zacks-manifesto" replace />} />
            <Route path="/zero-log-manifesto" element={<ZeroLogManifesto />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
            <Route path="/terms-of-service" element={<LegacyTermsRedirect />} />
            <Route path="/acceptable-use-policy" element={<AcceptableUsePolicy />} />
            <Route path="/customer-care" element={<CustomerCareComplaints />} />
            <Route path="/complaints" element={<Navigate to="/customer-care" replace />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/vulnerable-persons-policy" element={<VulnerablePersonsPolicy />} />
            <Route path="/adr" element={<ADRPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/ai-catalogue" element={<AICatalogue />} />
            <Route path="/services/no-account-vpn" element={<IntentLandingPage />} />
            <Route path="/services/travel-esim" element={<IntentLandingPage />} />
            <Route path="/services/virtual-numbers" element={<IntentLandingPage />} />
            <Route path="/services/holiday-esim-data" element={<IntentLandingPage />} />
            <Route path="/services/business-travel-connectivity" element={<IntentLandingPage />} />
            <Route path="/services/business-virtual-number" element={<IntentLandingPage />} />
            <Route path="/guides/avoid-roaming-charges-with-esim" element={<IntentLandingPage />} />
            <Route path="/compare/pay-as-you-go-vpn-vs-subscription" element={<IntentLandingPage />} />
            <Route path="/learn/temporary-identity-systems" element={<IntentLandingPage />} />
            <Route path="/learn/privacy-architecture" element={<PrivacyArchitecture />} />
            <Route path="/travel/flying-abroad" element={<TravelCampaignPage />} />
            <Route path="/travel/ferry-and-cruise" element={<TravelCampaignPage />} />
            <Route path="/travel/on-the-road" element={<TravelCampaignPage />} />
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        <Footer blackBackground={useContinuousBlackBackground} showCookieSettings={!['/portal', '/success'].includes(pathname)} />
        <Toaster />
      </div>
    </>
  );
};

export default App;
